import { isFinalStage, type ShipmentRow } from "@estelle/domain";
import { EscalatedLabel } from "@/modules/orders/screens/EscalatedLabel";
import type { ReactNode } from "react";

import { formatDate } from "@/components/table/cells";
import {
  ExactTime,
  HealthLabel,
  StageBadge,
} from "@/modules/orders/screens/TrackingParts";
import { attemptsLabel, needsAttention } from "@/modules/orders/tracking";

/**
 * One shipment on the phone (#527, Part B): the order and the courier on a line,
 * the tracking number under it, then the two things a courier queue is read
 * for — where the parcel is and whether it needs a look — and what to know to
 * judge it: when it last moved, when it is due, how many attempts.
 *
 * The generic card a list gets for free is every chosen column as a label and a
 * value, which for Shipments was eleven rows a shipment, with the stage fifth.
 * This puts the stage and the health where the eye lands, and like the Orders
 * card it is drawn from the row, not from the column chooser.
 */
export function ShipmentMobileCard({ row }: { row: ShipmentRow }) {
  const open = row.stage !== null && !isFinalStage(row.stage);
  const facts: ReactNode[] = [];

  if (open && row.lastScanAt) {
    facts.push(
      <span key="last">
        Last update <ExactTime value={row.lastScanAt} />
      </span>,
    );
  }
  if (open && row.expectedDeliveryDate) {
    facts.push(
      <span key="due">
        Expected {formatDate(row.expectedDeliveryDate, false)}
      </span>,
    );
  }
  if (row.attemptCount > 0) {
    facts.push(<span key="tries">{attemptsLabel(row.attemptCount)}</span>);
  }

  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate text-sm font-medium">{row.orderNumber}</span>
        <span className="text-muted-foreground min-w-0 truncate text-sm">
          {row.courier ?? "Courier not set"}
        </span>
      </div>
      {row.trackingNumber ? (
        <p className="text-muted-foreground mt-0.5 text-sm tabular-nums wrap-anywhere">
          {row.trackingNumber}
        </p>
      ) : (
        <p className="text-muted-foreground mt-0.5 text-sm">
          No tracking number
        </p>
      )}
      {row.stage ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <StageBadge stage={row.stage} />
          {needsAttention(row.health) ? (
            <HealthLabel health={row.health} />
          ) : null}
          {row.openEscalation ? <EscalatedLabel /> : null}
        </div>
      ) : null}
      {facts.length > 0 ? (
        <p className="text-muted-foreground mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-sm">
          {facts}
        </p>
      ) : null}
      <p className="text-muted-foreground mt-1 text-sm">
        {[
          row.destinationState,
          row.shippedAt ? `Shipped ${formatDate(row.shippedAt, false)}` : null,
          row.deliveredAt
            ? `Delivered ${formatDate(row.deliveredAt, false)}`
            : null,
        ]
          .filter((part): part is string => part !== null && part !== "")
          .join(" · ")}
      </p>
    </div>
  );
}
